import { createContext, useContext, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { fitBox } from './fit';

/*
 * What a workbench mode places its parts with (docs/ANALYSIS_WORKBENCH.md
 * section 3). Every part is a portal into one of the shell's places, so a
 * mode keeps its state in one React tree however its parts are spread over
 * the screen.
 *
 * Outside a WorkbenchShell (a component's own test) each part renders where
 * it stands, so the pieces still work on their own.
 */
export const WorkbenchContext = createContext(null);

/** The places a mode may fill: the top bar, the stage's overlays, the toolbar's slots, the floating options. */
export const SLOT_NAMES = Object.freeze([
  'context', 'stage', 'stageTop', 'status', 'overlay', 'roi', 'tools', 'view', 'target', 'edit', 'save', 'float',
]);

const NO_DRAWERS = Object.freeze({ isOpen: () => false, open: () => {}, close: () => {}, toggle: () => {} });
const OUTSIDE = Object.freeze({
  inShell: false, compact: false, stageSize: null, slot: () => null, drawers: NO_DRAWERS,
  dirty: false, setDirty: () => {}, guard: (proceed) => { proceed(); return true; }, busy: false,
  floatHidden: null, hideFloat: () => {},
});

/**
 * The shell, as a mode sees it: {inShell, compact, stageSize, slot(name),
 * drawers: {isOpen(id), open(id, {focus}), close(id), toggle(id)}, dirty,
 * setDirty(bool), guard(proceed), busy, floatHidden, hideFloat(tool)}. `focus` is true (the drawer's title), false,
 * or a function giving the element to focus once the drawer is shown (at once
 * when it is open already). guard runs `proceed` at once while nothing
 * is unsaved, else once the person chooses Discard (Keep working runs
 * nothing): what would replace the work on the stage goes through it.
 */
export function useWorkbench() {
  return useContext(WorkbenchContext) || OUTSIDE;
}

/**
 * Unsaved work, reported to the shell while `dirty` is true (and no longer
 * once the component is gone): the way out then asks first, and the browser's
 * close or reload gets its prompt (docs/ANALYSIS_WORKBENCH.md section 5b).
 * Each caller is one source; the shell has unsaved work while any has.
 */
export function useUnsavedWork(dirty) {
  const report = useContext(WorkbenchContext)?.reportDirty;
  const id = useId();
  useEffect(() => { report?.(id, !!dirty); }, [report, id, dirty]);
  useEffect(() => () => report?.(id, false), [report, id]);
}

/**
 * Work under way (a decode, a save), reported to the shell while `busy` is
 * true: the way out (Back, the overlay's ×, Escape) is held until it ends, as
 * the thermal panel's dialog held its Close. Each caller is one source.
 */
export function useWorkBusy(busy) {
  const report = useContext(WorkbenchContext)?.reportBusy;
  const id = useId();
  useEffect(() => { report?.(id, !!busy); }, [report, id, busy]);
  useEffect(() => () => report?.(id, false), [report, id]);
}

/** Children placed in one of the shell's places (SLOT_NAMES). */
export function WorkbenchSlot({ name, children }) {
  const wb = useContext(WorkbenchContext);
  if (!wb) return children ?? null;
  const node = wb.slot(name);
  return node ? createPortal(children, node) : null;
}

/**
 * The fit box (section 6): children drawn at `size`'s aspect, contained in
 * the stage and centred. A square until the picture's size is known.
 */
export function WorkbenchStage({ size, children, testId = 'wb-fit' }) {
  const wb = useContext(WorkbenchContext);
  if (!wb) return <div data-testid={testId}>{children}</div>;
  const node = wb.slot('stage');
  if (!node) return null;
  const box = fitBox(wb.stageSize, size);
  const style = box
    ? { position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height }
    : { position: 'absolute', inset: 0 };
  return createPortal(
    <div className="wb-fit" data-testid={testId} data-fit={box ? `${Math.round(box.width)}x${Math.round(box.height)}` : ''}
         style={style}>
      {children}
    </div>,
    node,
  );
}

/**
 * A toolbar button: its icon, and its label -- shown where the toolbar has
 * room for it (a phone's popovers, a wide screen), always its accessible name,
 * and the workbench's tooltip where it is hidden (./WorkbenchTooltip.js).
 * `title` is a longer description, said by the tooltip after the name. On a
 * disabled button (why it is disabled) it stays the browser's own title too:
 * a mouse never hovers a disabled button for the workbench's tooltip, and a
 * screen reader reads it as the description. A tool with floating options
 * names itself with data-wb-tool, which the options sit beside (ToolOptions).
 */
export function ToolButton({ icon, label, title = null, className = '', children, ...rest }) {
  const tip = title && title !== label ? title : undefined;
  return (
    <button type="button" className={`wb-btn ${className}`.trim()} aria-label={label} data-tip={tip}
            title={rest.disabled ? tip : undefined} {...rest}>
      {icon && <span aria-hidden="true" className="wb-icon">{icon}</span>}
      <span className="wb-label">{label}</span>
      {children}
    </button>
  );
}

/**
 * A tool's floating options (docs/ANALYSIS_WORKBENCH.md section 4b): the
 * settings of the tool in hand -- Magic fill's reach, a brush's size --
 * floating over the stage's bottom edge beside the tool's button
 * (`[data-wb-tool="<tool>"]`; on a phone, the tool palette's button), so they
 * can be changed while watching the picture. Rendered while the tool is in
 * hand; × or Escape inside them hides them until the tool is put down and
 * taken up again. `more` names the drawer holding the rest (its ⋯ opens it). The same
 * settings stay in that drawer: these are a second way to them, not the only
 * one. Outside a shell they render where they stand.
 */
export function ToolOptions({ tool, icon = null, label, more = null, testId, children }) {
  const wb = useContext(WorkbenchContext);
  const { t } = useTranslation('workbench');
  const place = wb?.placeFloat;
  const unhide = wb?.unhideFloat;
  // Placed beside the tool after each of its renders (its size may have changed); on the way out, let go,
  // and forget an × on them: the tool taken up again by any way (its button, Pan's return, the eyedropper's)
  // shows its options again.
  useLayoutEffect(() => { place?.(); });
  useLayoutEffect(() => () => { place?.({ gone: tool }); unhide?.(tool); }, [place, unhide, tool]);
  if (wb && wb.floatHidden === tool) return null;
  const panel = (
    <div className="wb-float-panel" role="group" aria-label={t('float.label', { tool: label })} data-wb-float={tool}
         data-testid={testId || `wb-float-${tool}`}>
      <span className="wb-float-title">
        {icon && <span aria-hidden="true" className="wb-icon">{icon}</span>}
        <span>{label}</span>
      </span>
      {children}
      {wb && more && (
        <button type="button" className="wb-btn" aria-label={t('float.more', { tool: label })}
                data-testid={`wb-float-more-${tool}`} onClick={() => wb.drawers.open(more)}>
          <span aria-hidden="true">⋯</span>
        </button>
      )}
      {wb && (
        <button type="button" className="wb-btn wb-close" aria-label={t('float.hide', { tool: label })}
                data-testid={`wb-float-hide-${tool}`} onClick={() => wb.hideFloat(tool)}>
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
  if (!wb) return panel;
  const node = wb.slot('float');
  return node ? createPortal(panel, node) : null;
}
